import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { DiscoverItem, Trip } from "../../types";
import { buildIcsFile, buildShareUrl, downloadIcs } from "./trip-export";

import { Icon } from "@majourneys/icons";
interface TripShareMenuProps {
  readonly trip: Trip;
  readonly resolveItem: (itemId: string) => DiscoverItem | null;
}

type CopyState = "idle" | "copied" | "failed";

/**
 * Share dropdown — lives in the trip detail top bar. Three items:
 *   1. Copy shareable link  (URL with trip payload encoded in the
 *      query string; opening it re-hydrates the trip)
 *   2. Download .ics         (calendar file with one VEVENT per stop)
 *   3. Print itinerary       (window.print() with print-only styles)
 */
export function TripShareMenu({ trip, resolveItem }: TripShareMenuProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [copyState, setCopyState] = useState<CopyState>("idle");
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const handleCopyLink = async () => {
    const url = buildShareUrl(trip);
    try {
      await navigator.clipboard.writeText(url);
      setCopyState("copied");
    } catch {
      // Fallback: legacy execCommand copy via a temporary textarea.
      try {
        const ta = document.createElement("textarea");
        ta.value = url;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
        setCopyState("copied");
      } catch {
        setCopyState("failed");
      }
    }
    setTimeout(() => setCopyState("idle"), 2200);
  };

  const handleDownloadIcs = () => {
    const ics = buildIcsFile(trip, resolveItem);
    downloadIcs(trip, ics);
    setOpen(false);
  };

  const handlePrint = () => {
    setOpen(false);
    // Give the popover a tick to unmount before the print dialog
    // captures the DOM.
    setTimeout(() => window.print(), 80);
  };

  return (
    <div className="mj-trip-share" ref={wrapRef}>
      <button
        type="button"
        className="mj-trip-share__trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("trip.share_export")}
      >
        <Icon name="ri-share-forward-line" aria-hidden="true" />
      </button>
      {open && (
        <div role="menu" className="mj-trip-share__popover">
          <button
            type="button"
            role="menuitem"
            className="mj-trip-share__item"
            onClick={handleCopyLink}
          >
            <Icon name={copyState === "copied" ? "ri-check-line" : "ri-link"} aria-hidden="true" />
            {copyState === "copied"
              ? t("trip.link_copied")
              : copyState === "failed"
                ? t("trip.copy_failed")
                : t("trip.copy_link")}
          </button>
          <button
            type="button"
            role="menuitem"
            className="mj-trip-share__item"
            onClick={handleDownloadIcs}
          >
            <Icon name="ri-calendar-2-line" aria-hidden="true" />
            {t("trip.download_ics")}
          </button>
          <button
            type="button"
            role="menuitem"
            className="mj-trip-share__item"
            onClick={handlePrint}
          >
            <Icon name="ri-printer-line" aria-hidden="true" />
            {t("trip.print_itinerary")}
          </button>
        </div>
      )}
    </div>
  );
}
